"use client";

import Card from "@/components/Card";
import OverviewCard from "@/components/OverviewCard";
import PrimaryCard from "@/components/PrimaryCard";
import SecondaryCard from "@/components/SecondaryCard";
import Staking from "@/components/Staking";
import StakingTab from "@/components/StakingTab";
import Unstaking from "@/components/Unstaking";
import Withdraw from "@/components/Withdraw";
import ExploreSpheron from "@/components/ExploreSpheron";
import {
  getStSPONBalance,
  getSPONBalance,
  getTotalStakedSPON,
  getWithdrawRequestDetails,
} from "@/services/staking";
import {
  getPoolData,
  getWithdrawalRequestsForAddress,
} from "@/services/subgraph";
import { getCachedSPONPrice } from "@/services/price";
import { getApr } from "@/utils";
import { fetchAPRData, getDefaultAPR } from "@/services/apr";
import dayjs from "dayjs";
import { formatEther } from "ethers";
import { useEffect, useState } from "react";
import { useActiveAccount } from "thirdweb/react";
import { getActiveDexes } from "@/config/dexes";
import {
  isStakingDeprecated,
  STAKING_PAUSED_MESSAGE,
  STAKING_PAUSED_TITLE,
} from "@/config";
import DollarIcon from "@/assets/dollar.svg";
import LinkIcon from "@/assets/link-external.svg";

const tabList = [
  { id: 0, label: "STAKE" },
  { id: 1, label: "UNSTAKE" },
  { id: 2, label: "WITHDRAW" },
];

const activeDexes = getActiveDexes();

export default function Home() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [stSponBalance, setStSponBalance] = useState<number | null>(null);
  const [sponBalance, setSponBalance] = useState<number | null>(null);
  // const [convertedAsset, setConvertedAsset] = useState<number | null>(null);
  const [totalStakedSpon, setTotalStakedSpon] = useState<number | null>(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState<boolean>(true);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);
  const [isAprLoading, setIsAprLoading] = useState<boolean>(true);
  const [poolData, setPoolData] = useState<{
    activeStakers: number;
    totalDepositBalance: number;
  }>({ activeStakers: 0, totalDepositBalance: 0 });
  const [sponPrice, setSponPrice] = useState<number>(0);
  const [aprData, setAprData] = useState<{
    apy: number;
    apr: number;
    cronSchedule: string;
    lastDistributedAmount: string;
    lastDistributionTimestamp: string;
  } | null>(null);

  const activeAccount = useActiveAccount();

  const [withdrawRequest, setWithdrawRequest] = useState<{
    unlockTime: number;
  } | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [subgraphData, setSubgraphData] = useState<Awaited<
    ReturnType<typeof getWithdrawalRequestsForAddress>
  > | null>(null);

  const resetAccountDetails = () => {
    setStSponBalance(null);
    setSponBalance(null);
    setWithdrawRequest(null);
    setSubgraphData(null);
    // setConvertedAsset(null);
  };

  const fetchAPRDataLocal = async () => {
    // No rewards are distributed anymore, so there is no APR/APY to fetch.
    if (isStakingDeprecated) {
      setIsAprLoading(false);
      return;
    }
    setIsAprLoading(true);
    try {
      const aprDataResult = await fetchAPRData().catch(() => getDefaultAPR());
      setAprData(aprDataResult);
    } catch (error) {
      console.error("Error fetching APR data:", error);
    } finally {
      setIsAprLoading(false);
    }
  };

  useEffect(() => {
    if (!activeAccount?.address) resetAccountDetails();

    const fetchBalance = async () => {
      setIsBalanceLoading(true);
      try {
        if (activeAccount) {
          const [sponBal, stSponBal] = await Promise.all([
            getSPONBalance(activeAccount),
            getStSPONBalance(activeAccount),
          ]);

          setStSponBalance(Number(formatEther(stSponBal)));
          setSponBalance(Number(formatEther(sponBal)));
          const withdrawRequest = await getWithdrawRequestDetails(
            activeAccount
          );
          const subgraphData = await getWithdrawalRequestsForAddress(
            activeAccount.address
          );

          // const convertedAssets = await fetchConvertToAssets(
          //   BigInt(subgraphData.depositBalance)
          // );

          // setConvertedAsset(Number(formatEther(convertedAssets)));

          setSubgraphData(subgraphData);

          setWithdrawRequest({
            unlockTime: Number(withdrawRequest[1]),
          });
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
      } finally {
        setIsBalanceLoading(false);
      }
    };

    fetchBalance();
    document.addEventListener("refreshBalance", fetchBalance);
    return () => {
      document.removeEventListener("refreshBalance", fetchBalance);
    };
  }, [activeAccount]);

  useEffect(() => {
    const fetchTotalStats = async () => {
      setIsStatsLoading(true);
      setIsAprLoading(true);
      try {
        const [totalStaked, poolData, sponPrice, aprDataResult] =
          await Promise.all([
            getTotalStakedSPON(),
            getPoolData(),
            getCachedSPONPrice(),
            isStakingDeprecated
              ? Promise.resolve(null)
              : fetchAPRData().catch(() => getDefaultAPR()),
          ]);
        setTotalStakedSpon(Number(formatEther(totalStaked)));
        setPoolData(poolData);
        setSponPrice(sponPrice);
        setAprData(aprDataResult);
      } catch (error) {
        console.error("Error fetching balance:", error);
      } finally {
        setIsStatsLoading(false);
        setIsAprLoading(false);
      }
    };

    fetchTotalStats();
    document.addEventListener("refreshStats", fetchTotalStats);
    document.addEventListener("refreshAPR", fetchAPRDataLocal);
    return () => {
      document.removeEventListener("refreshStats", fetchTotalStats);
      document.removeEventListener("refreshAPR", fetchAPRDataLocal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let formattedRemainingTime;
  let endTime = dayjs();
  let isInCooldown = false;
  let remainingTime = dayjs.duration({ minutes: 0 });
  if (withdrawRequest) {
    endTime = dayjs(withdrawRequest?.unlockTime * 1000);
    remainingTime = dayjs.duration(endTime.diff(dayjs()));

    const days = Math.floor(remainingTime.asDays());
    const hours = remainingTime.hours();
    const minutes = remainingTime.minutes();

    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days} days`);
    }
    if (hours > 0) {
      parts.push(`${hours} hrs`);
    }
    if (minutes > 0 || parts.length === 0) {
      parts.push(`${minutes} mins`);
    }

    formattedRemainingTime = parts.join(": ");

    isInCooldown = endTime.isAfter(dayjs());
  }

  const apyPercent = isStakingDeprecated
    ? 0
    : aprData?.apy ?? getApr(totalStakedSpon) ?? "-";

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PrimaryCard
          title={"My Staked SPON"}
          description={
            <>
              {activeAccount?.address && stSponBalance ? (
                <>{stSponBalance.toFixed(2)} stSPON</>
              ) : (
                "N/A"
              )}
            </>
          }
          tagTitle="Wallet Balance:"
          tagDescription={`${
            activeAccount?.address
              ? `${(sponBalance || 0).toLocaleString("en-US")} SPON`
              : "N/A"
          }`}
          showTag={!!activeAccount?.address}
          backgroundImageUrl="/staked-card-bg.svg"
          tagLoading={isBalanceLoading}
          loading={isBalanceLoading}
        />
        <PrimaryCard
          title={"Total Staked SPON"}
          // description={`${
          //   totalStakedSpon
          //     ? `${totalStakedSpon.toLocaleString("en-US")} stSPON`
          //     : "N/A"
          // }`}
          description={`${poolData.totalDepositBalance.toLocaleString(
            "en-US"
          )} SPON`}
          tagTitle="Cooldown Status:"
          tagDescription={
            isInCooldown ? formattedRemainingTime : "No active cooldown"
          }
          showTag={false}
          backgroundImageUrl="/reward-card-bg.svg"
          tagLoading={false}
          loading={isStatsLoading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-4">
          {isStakingDeprecated ? (
            <SecondaryCard
              title={STAKING_PAUSED_TITLE}
              description={
                <span className="block text-sm font-normal leading-[18px] text-[#EAA13B]">
                  {STAKING_PAUSED_MESSAGE}
                </span>
              }
              cardClassname="bg-[#EAA13B1F] !border-y-0 !border-r-0 !border-l-2 !border-warning !py-5 !gap-2"
              loading={false}
            />
          ) : (
            <SecondaryCard
              title={"Current APY"}
              description={apyPercent + "%"}
              cardClassname="bg-success-background !border-0"
              loading={isAprLoading}
              loadingWidth="10%"
            />
          )}
          <SecondaryCard
            title={"Total Value Locked (TVL)"}
            description={
              totalStakedSpon && sponPrice
                ? `$${(totalStakedSpon * sponPrice).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : "-"
            }
            loading={isStatsLoading}
          />
          <SecondaryCard
            title={"Active Stakers"}
            description={poolData.activeStakers}
            loading={isStatsLoading}
          />
          <SecondaryCard
            title={"Exchange Rate"}
            description={"1 SPON = 1 stSPON"}
            loading={isStatsLoading}
          />
        </div>
        <div className="py-4 px-4 md:px-6 flex flex-col gap-3 border border-card-border bg-card-background">
          <StakingTab
            tabList={tabList}
            selectedTab={selectedTab}
            defaultTab={0}
            onClick={(id) => {
              setSelectedTab(id);
            }}
          />
          {
            {
              0: (
                <Staking
                  apyPercent={apyPercent}
                  isLoading={isBalanceLoading}
                  sponBalance={sponBalance}
                  isApyLoading={isAprLoading}
                  switchTab={(tab: "stake" | "unstake") => {
                    setSelectedTab(tab === "stake" ? 0 : 1);
                  }}
                />
              ),
              1: (
                <Unstaking
                  isLoading={isBalanceLoading}
                  stSponBalance={stSponBalance}
                />
              ),
              2: (
                <Withdraw
                  switchTab={(tab: "stake" | "unstake") => {
                    setSelectedTab(tab === "stake" ? 0 : 1);
                  }}
                />
              ),
            }[selectedTab]
          }
        </div>
      </div>

      <OverviewCard
        title={"Overview"}
        description={
          isStakingDeprecated ? (
            <>
              SPON staking has been <span className="text-white">paused</span>.
              New deposits are no longer accepted and no staking rewards are
              being distributed anymore. If you have staked before, you can
              unstake your <span className="text-white">stSPON</span> and
              withdraw all of your <span className="text-white">SPON</span> once
              the cooldown period ends.
            </>
          ) : (
            <>
              Stake your <span className="text-white"> SPON</span> tokens to
              help secure the Spheron Network and earn rewards in return.
              Spheron is building the world's largest community-powered data
              center, and be part of the movement and shape the future of
              decentralized compute.
            </>
          )
        }
      />
      <OverviewCard
        title={"What is stSPON?"}
        description={
          isStakingDeprecated ? (
            <>
              <span className="text-white">stSPON</span> is the token you
              received in your wallet when you staked{" "}
              <span className="text-white">SPON</span>. You can unstake it
              anytime, which starts a 14 days cooldown period, after which you
              can withdraw your <span className="text-white">SPON</span> back.
            </>
          ) : (
            <>
              Upon Staking, you will directly get{" "}
              <span className="text-white">stSPON</span> to your wallet, which
              you can unstake anytime but will have a 14 days cooldown period
              and claim rewards separately. Staked{" "}
              <span className="text-white">SPON</span> token that earn staking
              rewards while locked.
            </>
          )
        }
      />

      <Card className="w-full self-start flex flex-col gap-4 p-4 !px-4 !items-start">
        <h2 className="text-2xl md:font-semibold flex items-center gap-2">
          <DollarIcon className="h-6 w-6" /> BUY SPON
        </h2>
        <div className="flex gap-4 w-full flex-col md:flex-row">
          {activeDexes.map((item) => {
            return (
              <a
                key={item.id}
                className="basis-[24%]"
                href={item.link}
                target="_blank"
              >
                <Card className="text-white hover:border-[#4C4C40] flex !items-center gap-2 !px-4 !py-2 !bg-black">
                  {item.logo}
                  {item.name}
                  <LinkIcon className="ml-auto w-4 h-4" />
                </Card>
              </a>
            );
          })}
        </div>
      </Card>

      <ExploreSpheron isEligible />

      {/* <Note
        title="⚠️️️ Important Note"
        description={
          <>
            Your unstaked tokens are in the{" "}
            <span className="font-bold text-white">14-day cooldown period</span>.
            After this period ends, you'll be able to withdraw your SPON.
          </>
        }
      />
      <div className="grid grid-cols-2 gap-4">
       
      </div>
      <ClaimRewardsModal close={() => setIsOpen(false)} isOpen={isOpen} /> */}
    </div>
  );
}
